<template>
  <div class="main-wrapper">
    <layouts></layouts>
    <sidebar></sidebar>
    <div class="page-wrapper">
      <div class="content">
        <pageaddheader :title="isCounts ? 'Stock Counts' : 'Stock Adjustments'" :title1="isCounts ? 'Count stock on the shelf and correct the difference' : 'Add or remove stock with a reason'" :items="headerItems" />

        <div class="card">
          <div class="card-body">
            <div class="sa-top">
              <div class="sa-view-tabs" role="tablist">
                <button type="button" role="tab" :class="{ active: !isCounts }" :aria-selected="!isCounts" @click="setView('')">
                  <i class="fas fa-sliders-h"></i> Adjustments
                </button>
                <button type="button" role="tab" :class="{ active: isCounts }" :aria-selected="isCounts" @click="setView('counts')">
                  <i class="fas fa-clipboard-check"></i> Stock counts
                </button>
              </div>
              <div class="sa-search">
                <i class="fa fa-search" aria-hidden="true"></i>
                <input v-model.trim="search" type="search" class="form-control" :placeholder="isCounts ? 'Search count no. or item...' : 'Search adjustment no., item or remarks...'" @input="debouncedLoad" />
              </div>
            </div>

            <div class="sa-filters">
              <div class="sa-status-tabs">
                <button v-for="tab in statusTabs" :key="tab.value" type="button" :class="{ active: status === tab.value }" @click="setStatus(tab.value)">
                  {{ tab.label }}
                </button>
              </div>
              <input v-model="dateFrom" type="date" class="form-control sa-date" title="From date" @change="reload" />
              <input v-model="dateTo" type="date" class="form-control sa-date" title="To date" @change="reload" />
              <button v-if="hasFilters" type="button" class="sa-clear" @click="clearFilters">Clear filters</button>
            </div>

            <div v-if="loading" class="sa-empty">Loading...</div>
            <div v-else-if="!rows.length" class="sa-empty">
              <strong>{{ hasFilters ? "Nothing matches these filters" : isCounts ? "No stock counts yet" : "No stock adjustments yet" }}</strong>
              <span v-if="!hasFilters">
                {{ isCounts ? "Count what is on the shelf; the system works out the difference and corrects stock." : "Use an adjustment to record damage, expiry, theft, items found or any other stock correction." }}
              </span>
              <router-link v-if="!hasFilters" :to="newLink" class="btn btn-submit mt-2">{{ isCounts ? "Start a stock count" : "New adjustment" }}</router-link>
            </div>
            <div v-else class="table-responsive">
              <table class="sa-table">
                <thead>
                  <tr>
                    <th>{{ isCounts ? "Count No." : "Adjustment No." }}</th>
                    <th>Date</th>
                    <th class="num">Items</th>
                    <th class="num">Added</th>
                    <th class="num">Removed</th>
                    <th class="num">Value change</th>
                    <th>Status</th>
                    <th class="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in rows" :key="row.adjustmentid">
                    <td>
                      <router-link :to="openLink(row)" class="sa-no">{{ row.adjustmentno }}</router-link>
                      <small v-if="row.remarks" class="sa-sub">{{ row.remarks }}</small>
                    </td>
                    <td>{{ formatDate(row.adjustmentdate) }}</td>
                    <td class="num">{{ row.itemcount }}</td>
                    <td class="num text-good">{{ row.totalinqty ? `+${formatQty(row.totalinqty)}` : "-" }}</td>
                    <td class="num text-bad">{{ row.totaloutqty ? `-${formatQty(row.totaloutqty)}` : "-" }}</td>
                    <td class="num" :class="row.valueimpact < 0 ? 'text-bad' : row.valueimpact > 0 ? 'text-good' : ''">
                      {{ row.valueimpact ? money(row.valueimpact, true) : "-" }}
                    </td>
                    <td><span class="sa-pill" :class="`pill-${row.status.toLowerCase()}`">{{ statusLabel(row.status) }}</span></td>
                    <td class="text-end sa-actions">
                      <router-link :to="openLink(row)" class="sa-act">{{ row.status === "DRAFT" ? "Edit" : "View" }}</router-link>
                      <button v-if="row.status === 'DRAFT'" type="button" class="sa-act sa-act-post" @click="postRow(row)">Post</button>
                      <button v-if="row.status !== 'CANCELLED'" type="button" class="sa-act sa-act-cancel" @click="cancelRow(row)">Cancel</button>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div class="sa-pager">
                <span>{{ rangeText }}</span>
                <div>
                  <button type="button" class="sa-page-btn" :disabled="page <= 1" @click="goPage(page - 1)">&lsaquo; Prev</button>
                  <button type="button" class="sa-page-btn" :disabled="page * pageSize >= total" @click="goPage(page + 1)">Next &rsaquo;</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";

export default {
  name: "stockadjustmentlist",
  data() {
    return {
      rows: [],
      total: 0,
      page: 1,
      pageSize: 15,
      loading: false,
      search: "",
      status: "",
      dateFrom: "",
      dateTo: "",
      searchTimer: null,
      statusTabs: [
        { value: "", label: "All" },
        { value: "DRAFT", label: "Draft" },
        { value: "POSTED", label: "Posted" },
        { value: "CANCELLED", label: "Cancelled" },
      ],
    };
  },
  computed: {
    isCounts() {
      return this.$route.query.view === "counts";
    },
    type() {
      return this.isCounts ? "STOCK_COUNT" : "ADJUSTMENT";
    },
    newLink() {
      return { path: "/stockadjustment", query: { type: this.type } };
    },
    headerItems() {
      return [{ text: this.isCounts ? "New Stock Count" : "New Adjustment", addname: `/stockadjustment?type=${this.type}` }];
    },
    hasFilters() {
      return Boolean(this.search || this.status || this.dateFrom || this.dateTo);
    },
    rangeText() {
      if (!this.total) return "";
      const start = (this.page - 1) * this.pageSize + 1;
      return `${start}-${Math.min(this.page * this.pageSize, this.total)} of ${this.total}`;
    },
  },
  watch: {
    "$route.query.view"() {
      this.page = 1;
      this.load();
    },
  },
  mounted() {
    this.load();
  },
  beforeUnmount() {
    clearTimeout(this.searchTimer);
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const { data } = await axios.get("/v1/stock-adjustments", {
          params: {
            page: this.page,
            page_size: this.pageSize,
            type: this.type,
            status: this.status || undefined,
            search: this.search || undefined,
            date_from: this.dateFrom || undefined,
            date_to: this.dateTo || undefined,
          },
        });
        this.rows = data.items || [];
        this.total = data.total || 0;
      } catch (error) {
        this.rows = [];
        Swal.fire({ icon: "error", title: "Could not load", text: error.response?.data?.detail || error.message });
      } finally {
        this.loading = false;
      }
    },
    reload() {
      this.page = 1;
      this.load();
    },
    debouncedLoad() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(this.reload, 300);
    },
    setView(view) {
      this.$router.replace({ path: "/stockadjustmentlist", query: view ? { view } : {} });
    },
    setStatus(value) {
      this.status = value;
      this.reload();
    },
    clearFilters() {
      this.search = "";
      this.status = "";
      this.dateFrom = "";
      this.dateTo = "";
      this.reload();
    },
    goPage(page) {
      this.page = page;
      this.load();
    },
    openLink(row) {
      return { path: "/stockadjustment", query: { id: row.adjustmentid } };
    },
    async postRow(row) {
      const result = await Swal.fire({
        icon: "question",
        title: `Post ${row.adjustmentno}?`,
        text: "Stock will be updated now. You can still cancel it later to reverse.",
        showCancelButton: true,
        confirmButtonText: "Yes, post",
      });
      if (!result.isConfirmed) return;
      try {
        await axios.post(`/v1/stock-adjustments/${row.adjustmentid}/post`);
        Swal.fire({ toast: true, position: "top-end", icon: "success", title: "Stock updated", showConfirmButton: false, timer: 1600 });
        this.load();
      } catch (error) {
        Swal.fire({ icon: "error", title: "Could not post", text: error.response?.data?.detail || error.message });
      }
    },
    async cancelRow(row) {
      const posted = row.status === "POSTED";
      const result = await Swal.fire({
        icon: "warning",
        title: `Cancel ${row.adjustmentno}?`,
        text: posted ? "The stock changes will be reversed." : "This draft will be cancelled.",
        showCancelButton: true,
        confirmButtonText: "Yes, cancel it",
      });
      if (!result.isConfirmed) return;
      try {
        await axios.post(`/v1/stock-adjustments/${row.adjustmentid}/cancel`);
        Swal.fire({ toast: true, position: "top-end", icon: "success", title: posted ? "Reversed" : "Cancelled", showConfirmButton: false, timer: 1600 });
        this.load();
      } catch (error) {
        Swal.fire({ icon: "error", title: "Could not cancel", text: error.response?.data?.detail || error.message });
      }
    },
    statusLabel(status) {
      return { DRAFT: "Draft", POSTED: "Posted", CANCELLED: "Cancelled" }[status] || status;
    },
    formatDate(value) {
      if (!value) return "-";
      return new Date(value).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    },
    formatQty(value) {
      return Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
    },
    money(value, signed = false) {
      const number = Number(value || 0);
      const text = `Rs. ${Math.abs(number).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      return signed ? `${number < 0 ? "-" : "+"}${text}` : text;
    },
  },
};
</script>

<style scoped>
.sa-top { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; margin-bottom: 14px; }
.sa-view-tabs { display: inline-flex; gap: 4px; padding: 4px; border-radius: 10px; background: #f3f4f6; }
.sa-view-tabs button { border: 0; background: transparent; padding: 8px 16px; border-radius: 8px; font-size: 14px; font-weight: 600; color: #6b7280; display: inline-flex; align-items: center; gap: 8px; }
.sa-view-tabs button.active { background: #fff; color: #c2410c; box-shadow: 0 1px 3px rgba(15, 23, 42, 0.1); }
.sa-search { position: relative; flex: 0 1 360px; }
.sa-search i { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #9ca3af; }
.sa-search input { padding-left: 34px; height: 38px; border-radius: 8px; }
.sa-filters { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 14px; }
.sa-status-tabs { display: inline-flex; padding: 3px; border: 1px solid #e5e7eb; border-radius: 8px; background: #f9fafb; }
.sa-status-tabs button { border: 0; background: transparent; padding: 6px 14px; border-radius: 6px; font-size: 13px; font-weight: 600; color: #6b7280; }
.sa-status-tabs button.active { background: #fff; color: #c2410c; box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08); }
.sa-date { width: 160px; height: 36px; border-radius: 8px; }
.sa-clear { border: 0; background: #f3f4f6; border-radius: 8px; padding: 0 14px; height: 36px; font-size: 13px; font-weight: 600; color: #374151; }
.sa-empty { display: flex; flex-direction: column; align-items: center; gap: 6px; padding: 48px 16px; color: #6b7280; text-align: center; font-size: 14px; }
.sa-empty strong { color: #111827; font-size: 16px; }
.sa-table { width: 100%; border-collapse: collapse; }
.sa-table th, .sa-table td { padding: 11px 12px; border-bottom: 1px solid #f1f2f4; font-size: 13.5px; vertical-align: middle; color: #374151; }
.sa-table th { background: #f9fafb; color: #6b7280; font-size: 11.5px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; white-space: nowrap; }
.sa-table tbody tr:hover td { background: #fffaf5; }
.sa-table .num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
.sa-no { font-weight: 600; color: #c2410c; }
.sa-sub { display: block; color: #9ca3af; font-size: 12px; max-width: 280px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.text-good { color: #15803d; }
.text-bad { color: #dc2626; }
.sa-pill { display: inline-block; padding: 2px 10px; border-radius: 999px; font-size: 12px; font-weight: 600; }
.pill-draft { background: #ffedd5; color: #c2410c; }
.pill-posted { background: #dcfce7; color: #15803d; }
.pill-cancelled { background: #f3f4f6; color: #6b7280; }
.sa-actions { white-space: nowrap; }
.sa-act { display: inline-block; border: 0; border-radius: 7px; padding: 5px 11px; margin-left: 4px; font-size: 12.5px; font-weight: 600; background: #eff6ff; color: #2563eb; }
.sa-act-post { background: #f0fdf4; color: #15803d; }
.sa-act-cancel { background: #fef2f2; color: #dc2626; }
.sa-pager { display: flex; align-items: center; justify-content: space-between; padding-top: 12px; font-size: 13px; color: #6b7280; }
.sa-page-btn { border: 1px solid #e5e7eb; background: #fff; border-radius: 7px; padding: 5px 12px; margin-left: 6px; font-size: 13px; }
.sa-page-btn:disabled { opacity: 0.5; }
</style>
